import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Button, Card, Typography, Tag, message, Input, Row, Col } from 'antd';
import { BarChartOutlined, SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import BasicLayout from '@layout/basic';
import API from '@/api/api'; // downloadProjectProgressExcel 함수에서 사용
import { ProjectStatusCodeColor } from '@constants/status';
import { useApi } from '@/hooks/useApi';

const { Title } = Typography;

export default function AdminEstimateProjects() {
  const navigate = useNavigate();
  const [isProgressDownloading, setProgressDownloading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filteredProjects, setFilteredProjects] = useState([]);

  // useApi 훅으로 데이터 로딩, 에러, 새로고침 처리
  const { data: projects, loading, error, refetch } = useApi('/admin/estimates/projects/');

  // API 에러 처리
  useEffect(() => {
    if (error) {
      message.error('산정 프로젝트 목록 조회 실패');
      console.error('Error fetching projects:', error);
    }
  }, [error]);

  // API에서 데이터를 가져오거나 검색어가 변경될 때 필터링 수행
  useEffect(() => {
    if (projects) {
      if (!searchText.trim()) {
        setFilteredProjects(projects);
      } else {
        const filtered = projects.filter(
          (project) =>
            project.project_name_ko?.toLowerCase().includes(searchText.toLowerCase()) ||
            project.project_code?.toLowerCase().includes(searchText.toLowerCase())
        );
        setFilteredProjects(filtered);
      }
    }
  }, [projects, searchText]);

  const columns = [
    {
      title: '프로젝트',
      dataIndex: 'project_name',
      key: 'project_name',
      width: 200,
      render: (_, record) => (
        <>
          <Tag>{record.project_id}</Tag>
          {record.project_name} <br />({record.project_code})
        </>
      ),
    },
    {
      title: '진행상태',
      dataIndex: 'project_status',
      key: 'project_status',
      width: 120,
      align: 'center',
      render: (status) => (
        <Tag color={ProjectStatusCodeColor[status]} className="me-0">
          {status}
        </Tag>
      ),
    },
    {
      title: '총 기업수',
      dataIndex: 'total_companies',
      key: 'total_companies',
      width: 120,
      align: 'center',
      render: (total_companies) => (total_companies || 0) + ' 개',
    },
    {
      title: '총 참여 기업수',
      dataIndex: 'requested_companies',
      key: 'requested_companies',
      width: 120,
      align: 'center',
      render: (requested_companies) => (requested_companies || 0) + ' 개',
    },
    {
      title: '기업 현황',
      children: [
        {
          title: '미시작',
          key: 'company_not_started',
          width: 80,
          align: 'center',
          render: (_, record) => (record.companies_status?.NOT_STARTED || 0) + ' 개',
        },
        {
          title: '진행중',
          key: 'company_started',
          width: 80,
          align: 'center',
          render: (_, record) => (record.companies_status?.STARTED || 0) + ' 개',
        },
        {
          title: '완료',
          key: 'company_done',
          width: 80,
          align: 'center',
          render: (_, record) => (record.companies_status?.DONE || 0) + ' 개',
        },
      ],
    },
    {
      title: '총 사업장수',
      dataIndex: 'total_sites',
      key: 'total_sites',
      width: 120,
      align: 'center',
      render: (total_sites) => (total_sites || 0) + ' 개',
    },
    {
      title: '사업장 현황',
      children: [
        {
          title: '미시작',
          key: 'site_not_started',
          width: 80,
          align: 'center',
          render: (_, record) => (record.sites_status?.NOT_STARTED || 0) + ' 개',
        },
        {
          title: '진행중',
          key: 'site_started',
          width: 80,
          align: 'center',
          render: (_, record) => (record.sites_status?.STARTED || 0) + ' 개',
        },
        {
          title: '완료',
          key: 'site_done',
          width: 80,
          align: 'center',
          render: (_, record) => (record.sites_status?.DONE || 0) + ' 개',
        },
      ],
    },
    {
      title: '자세히 보기',
      key: 'action',
      width: 120,
      align: 'center',
      render: (_, record) => (
        <Button
          type="primary"
          icon={<BarChartOutlined />}
          onClick={() =>
            navigate(
              `/admin/estimates/projects/${record.project_id}?project_code=${record.project_code}`
            )
          }
        >
          사업장 보기
        </Button>
      ),
    },
  ];

  async function downloadProjectProgressExcel() {
    try {
      setProgressDownloading(true);
      const response = await API.get(`/admin/estimates/projects/`, {
        params: { format: 'file' },
        responseType: 'blob',
      });

      let filename = `프로젝트 진행 현황.xlsx`;
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename\*=UTF-8''(.+)/);
        if (filenameMatch?.[1]) {
          filename = decodeURIComponent(filenameMatch[1]);
        } else {
          const fallbackMatch = contentDisposition.match(/filename="(.+)"/);
          if (fallbackMatch?.[1]) {
            filename = fallbackMatch[1];
          }
        }
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      message.error(
        '프로젝트 진행 현황 엑셀 다운로드 중 장애가 발생했습니다. 개발팀에 문의해주세요'
      );
    } finally {
      setProgressDownloading(false);
    }
  }

  return (
    <BasicLayout>
      <Row gutter={[24, 24]} className="d-flex align-items-center justify-content-between mb-3">
        <Col xs={24} lg={12}>
          <Title level={2} className="mb-0">
            프로젝트 현황
          </Title>
          <Title level={5} className="mt-1 mb-0" style={{ color: '#666' }}>
            총 {filteredProjects.length}개 프로젝트
          </Title>
        </Col>
        <Col xs={24} lg={12}>
          <div style={{ textAlign: 'right' }}>
            <Button icon={<ReloadOutlined />} loading={loading} onClick={() => refetch()}>
              새로고침
            </Button>
          </div>
        </Col>
      </Row>

      <Card bodyStyle={{ paddingLeft: '10px', paddingRight: '10px' }}>
        <Row className="mb-3">
          <Col xs={24} lg={{ span: 8 }}>
            <Input.Search
              placeholder="프로젝트명 또는 프로젝트 코드로 검색"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={() => {}}
              prefix={<SearchOutlined />}
              allowClear
              size="large"
            />
          </Col>
          <Col xs={24} lg={{ span: 16 }}>
            <div className="d-flex justify-content-end align-items-end h-100 text-end">
              <Button
                loading={isProgressDownloading}
                onClick={() => downloadProjectProgressExcel()}
              >
                프로젝트 현황 엑셀 다운로드
              </Button>
            </div>
          </Col>
        </Row>
        <Row>
          <Col xs={24}>
            <Table
              columns={columns}
              dataSource={filteredProjects}
              rowKey="project_id"
              loading={loading}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]} - ${range[1]} of ${total} 프로젝트`,
              }}
              locale={{
                emptyText: searchText
                  ? `"${searchText}"에 대한 검색 결과가 없습니다.`
                  : '등록된 프로젝트가 없습니다.',
              }}
              bordered
            />
          </Col>
        </Row>
      </Card>
    </BasicLayout>
  );
}